import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { SITE_ORIGIN } from "@/data/site";
import { MAIN_SITE_URL } from "@/data/categories";
import JsonLd from "@/components/feature/JsonLd";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CategoryDropdown from "@/components/home/CategoryDropdown";
import CategoryGrid from "@/components/home/CategoryGrid";
import ArticleGrid from "@/components/home/ArticleGrid";

const HERO_IMAGE =
  "https://readdy.ai/api/search-image?query=A%20vibrant%20editorial%20flat%20lay%20of%20diverse%20fashion%20products%20including%20a%20coral%20handbag%20olive%20sneakers%20a%20mustard%20beanie%20and%20folded%20garments%20arranged%20in%20a%20dynamic%20diagonal%20composition%20on%20a%20warm%20sand%20and%20terracotta%20gradient%20backdrop%20soft%20dramatic%20side%20light%20premium%20ecommerce%20hero%20photography%20rich%20warm%20autumnal%20palette&width=1600&height=900&seq=hero-bg-01&orientation=landscape&nocache=true";

const ARTICLE_IMAGE =
  "https://readdy.ai/api/search-image?query=An%20open%20notebook%20with%20colorful%20category%20sticky%20notes%20and%20product%20reference%20photos%20arranged%20on%20a%20warm%20oak%20desk%20with%20a%20ceramic%20mug%20and%20reading%20glasses%20soft%20golden%20hour%20light%20cozy%20editorial%20workspace%20photography%20warm%20inviting%20atmosphere&width=1200&height=800&seq=article-cta-01&orientation=landscape&nocache=true";

const FAQ_ITEMS = [1, 2, 3, 4];

export default function Home() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const location = useLocation();

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: t("brand.name"),
    url: `${SITE_ORIGIN}/`,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_ORIGIN}/en?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: t("brand.name"),
    url: `${SITE_ORIGIN}/`,
    logo: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png",
  };

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 60);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd id="ld-website" data={JSON.stringify(websiteSchema)} />
      <JsonLd id="ld-organization" data={JSON.stringify(organizationSchema)} />

      <Navbar />

      {/* Hero */}
      <section className="relative flex min-h-[620px] w-full items-center justify-center overflow-hidden md:min-h-[720px]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Basetao Spreadsheet fashion product research"
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/45"></div>

        <div className="relative z-10 mx-auto w-full max-w-4xl px-4 py-24 text-center md:px-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-background-50/40 bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50 backdrop-blur">
            <i className="ri-sparkling-line"></i>
            {t("hero.badge")}
          </span>

          <h1 className="mt-6 text-3xl font-semibold leading-tight text-background-50 font-heading md:text-5xl">
            {t("hero.title")}
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-background-100 md:text-lg">
            {t("hero.subtitle")}
          </p>

          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to={`/${currentLang}/basetao-spreadsheet`}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-primary-600 sm:w-auto"
            >
              {t("cta.explore")}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-background-50 px-7 py-3.5 text-sm font-semibold text-foreground-900 whitespace-nowrap cursor-pointer transition hover:bg-background-100 sm:w-auto"
            >
              {t("cta.brand")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-background-50/60 px-7 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-background-50/10 sm:w-auto"
            >
              {t("cta.buyNow")}
            </a>
          </div>

          <a
            href="#categories"
            className="mt-12 inline-flex flex-col items-center gap-1 text-xs text-background-200"
          >
            <span>{t("hero.scroll")}</span>
            <i className="ri-arrow-down-line animate-bounce text-base"></i>
          </a>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-2xl font-semibold text-foreground-950 md:text-3xl">
            {t("categories.title")}
          </h2>
          <p className="mt-3 text-sm text-foreground-600 md:text-base">
            {t("categories.subtitle")}
          </p>
        </div>

        <div className="mt-8 flex justify-center">
          <CategoryDropdown />
        </div>

        <div className="mt-10">
          <CategoryGrid />
        </div>

        <div className="mt-10 text-center">
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-accent-600"
          >
            {t("categories.viewAll")}
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </div>
      </section>

      {/* About / text module */}
      <section className="bg-background-100">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:px-6 md:py-24">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-primary-600">
              {t("brand.name")}
            </span>
            <h2 className="mt-3 text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t("about.title")}
            </h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
              <p>{t("about.p3")}</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg">
            <div className="h-64 w-full md:h-80">
              <img
                src={ARTICLE_IMAGE}
                alt="Basetao Spreadsheet resources"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="rounded-lg border border-background-200 bg-background-50 p-8 text-center md:p-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-xs font-semibold text-secondary-900">
            <i className="ri-book-open-line"></i>
            {t("article.hint")}
          </span>
          <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-semibold text-foreground-950 md:text-3xl">
            {t("article.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-foreground-600 md:text-base">
            {t("article.subtitle")}
          </p>
          <Link
            to={`/${currentLang}/basetao-spreadsheet`}
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-primary-600"
          >
            {t("article.cta")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Pillar articles */}
      <section id="articles" className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t("articles.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 md:text-base">
              {t("articles.subtitle")}
            </p>
          </div>

          <div className="mt-10">
            <ArticleGrid />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-background-100">
        <div className="mx-auto max-w-3xl px-4 py-16 md:px-6 md:py-24">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-foreground-950 md:text-3xl">
              {t("faq.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600">{t("faq.subtitle")}</p>
          </div>

          <div className="mt-10 space-y-4">
            {FAQ_ITEMS.map((n) => (
              <details
                key={n}
                className="group rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-foreground-900 md:text-base">
                  {t(`faq.q${n}`)}
                  <i className="ri-add-line text-lg text-primary-600 transition group-open:rotate-45"></i>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  {t(`faq.a${n}`)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-4xl px-4 py-16 text-center md:px-6 md:py-20">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-shield-check-line text-xl"></i>
        </span>
        <h2 className="mt-4 text-xl font-semibold text-foreground-950 md:text-2xl">
          {t("trust.title")}
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
          {t("trust.text")}
        </p>
      </section>

      <Footer />
    </div>
  );
}